import '.a0{position:relative;}.ssr-plugin'
import '.h{width:100%;}.ssr-plugin'
import '.do{gap:2px;}.ssr-plugin'
import '.df{display:inline-flex;}.ssr-plugin'
import '.fa{justify-content:flex-end;}.ssr-plugin'
import '.b{align-items:center;}.ssr-plugin'
import '.de{outline:none;}.ssr-plugin'
import '.a9{pointer-events:none;}.ssr-plugin'
import '.a{display:flex;}.ssr-plugin'
import '.c{justify-content:center;}.ssr-plugin'
import '.gx{margin-left:-4px;}.ssr-plugin'
import '.a1{height:100%;}.ssr-plugin'
import '.o{background:transparent;}.ssr-plugin'
import '.p{border:none;}.ssr-plugin'
import '.e9{padding:0px;}.ssr-plugin'
import '.a2{position:absolute;}.ssr-plugin'
import '.c0{right:0px;}.ssr-plugin'
import '.gt{transform:translateY(100%);}.ssr-plugin'
import React, {
  forwardRef,
  KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from 'react'
import { radius } from '../../utils/radius.js'
import { Icon, IconProps } from '../Icon/index.js'
import { Text } from '../Text/index.js'
import { IconButton } from '../IconButton/index.js'
import { colors } from '../../tokens/colors.js'
import { KeyHint, KeyHintProps } from '../KeyHint/index.js'
import { Badge } from '../Badge/index.js'
import { styled, Style } from 'inlines'
import { useKeyboardShortcut } from '../../hooks/useKeyboardShortcut.js'
import { useTextDirection } from '../../hooks/useTextDirection.js'
import { shadows } from '../../utils/shadows.js'
import { TEXT_VARIANT_STYLES } from '../../tokens/typography.js'
import { LabelDescription } from '../LabelDescription/index.js'
import { mergeRefs } from '../../utils/mergeRefs.js'
import { Stack } from '../Stack/index.js'

type TextInputProps = {
  ref?: React.Ref<HTMLInputElement>
  value?: string
  onChange: (value: string) => void
  disabled?: boolean
  placeholder?: string
  prefix?: string | React.ReactNode
  leadIcon?: IconProps['variant']
  error?: boolean
  maxLength?: number
  clearable?: boolean
  suffix?: string | React.ReactNode
  keyHint?: KeyHintProps['hint']
  type?: 'text' | 'email' | 'password'
  size?: 'regular' | 'small'
  variant?: 'border' | 'ghost'
  isUrl?: boolean
  isPhone?: boolean
  onKeyDown?: (event: KeyboardEvent) => void
  autoFocus?: boolean
  onBlur?: React.FocusEventHandler<HTMLInputElement>
  pattern?: RegExp
  passwordToggle?: boolean
  style?: Style
  label?: string
  description?: string
  errorMessage?: string
  onFocus?: React.FocusEventHandler<HTMLInputElement>
}

const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      value,
      onChange,
      placeholder,
      error,
      errorMessage,
      disabled,
      prefix,
      leadIcon,
      clearable = true,
      maxLength,
      suffix,
      keyHint,
      type = 'text',
      size = 'regular',
      variant = 'border',
      isUrl = false,
      isPhone = false,
      autoFocus,
      onBlur,
      onFocus,
      onKeyDown,
      pattern,
      passwordToggle = true,
      label,
      description,
      style,
    },
    externalRef,
  ) => {
    const [internalError, setInternalError] = useState(false)
    const [hide, setHide] = useState(type === 'password')
    const length = typeof value !== 'string' ? 0 : value.length
    const innerRef = useRef<HTMLInputElement>(null)
    const direction = useTextDirection(value)

    const [internalValue, setInternalValue] = useState(value)
    const [focus, setFocus] = useState(false)
    const actualValue = internalValue ?? value

    useKeyboardShortcut(keyHint, () => setFocus(true))

    if (variant === 'border' && size === 'small')
      throw new Error('border variant doesnt support small size')
    if (variant === 'ghost' && error)
      throw new Error('ghost variant doesnt support error')

    useEffect(() => {
      setInternalValue(focus ? value : undefined)
      if (focus && type !== 'email') {
        innerRef.current.focus()
        innerRef.current.selectionStart = innerRef.current.value.length
      }
    }, [focus])

    return (
      <LabelDescription
        label={label}
        description={description}
        error={internalError && errorMessage}
      >
        <styled.div className="a0 h do df fa b de"
          onClick={() => innerRef.current.focus()}
          style={{
            borderRadius: radius[12],
            color: colors.neutral60,
            ...(size === 'regular' && {
              height: 36,
              padding: '0 10px',
            }),
            ...(size === 'small' && {
              height: 24,
              padding: '0 8px',
            }),
            ...(variant === 'border' && {
              border: `1px solid ${colors.neutral20Adjusted}`,
              ...(focus &&
                !disabled && {
                  background: colors.neutralInverted100,
                  border: `1px solid ${colors.neutral100}`,
                  outline: `4px solid ${colors.neutral20}`,
                }),
              ...((error || internalError) && {
                border: `1px solid ${colors.red80}`,
                ...(focus &&
                  !disabled && {
                    background: colors.neutralInverted100,
                    border: `1px solid ${colors.red100}`,
                    outline: `4px solid ${colors.red60}`,
                  }),
              }),
              ...(disabled && {
                color: colors.neutral20,
                background: colors.neutral5,
                border: `1px solid transparent`,
              }),
            }),
            ...(variant === 'ghost' && {
              border: '1px solid transparent ',
              background: 'transparent',
              ...(disabled && {
                color: colors.neutral20,
              }),
            }),
            '& input::placeholder': {
              userSelect: 'none',
              ...TEXT_VARIANT_STYLES['display-regular'],
              lineHeight: 'normal',
              overflowWrap: 'break-word',
              textRendering: 'optimizeLegibility',
              WebkitFontSmoothing: 'antialiased',
              MozOsxFontSmoothing: 'grayscale',
              WebkitTextSizeAdjust: '100%',
              MozTextSizeAdjust: '100%',
              textSizeAdjust: '100%',
              color: disabled ? colors.neutral20 : colors.neutral60,
            },
            '& input:-webkit-autofill': {
              WebkitBoxShadow: `0 0 0px 1000px ${colors.neutralInverted100} inset`,
              WebkitTextFillColor: `${colors.neutral100} !important`,
              caretColor: `${colors.neutral100} !important`,
            },
            '& input:-webkit-autofill:focus': {
              WebkitBoxShadow: `0 0 0px 1000px ${colors.neutralInverted100} inset`,
              WebkitTextFillColor: `${colors.neutral100} !important`,
            },
            ...style,
          }}
        >
          {leadIcon && type !== 'password' && (
            <span className="a9 a c b gx"
            >
              <Icon
                variant={leadIcon}
                size={size}
                color={disabled ? 'neutral20' : 'neutral60'}
              />
            </span>
          )}
          {isUrl &&
            !value?.includes('https://') &&
            !value?.includes('http://') && (
              <span
                style={{
                  marginRight: -4,
                  pointerEvents: typeof prefix === 'string' ? 'none' : 'auto',
                }}
              >
                <Text
                  color="inherit"
                  variant={
                    size === 'small' ? 'subtext-regular' : 'display-regular'
                  }
                >
                  https://
                </Text>
              </span>
            )}
          {prefix && (
            <>
              {typeof prefix === 'string' ? (
                <span
                  style={{
                    marginRight: -4,
                    pointerEvents: typeof prefix === 'string' ? 'none' : 'auto',
                  }}
                >
                  <Text
                    color="inherit"
                    variant={
                      size === 'small' ? 'subtext-regular' : 'display-regular'
                    }
                  >
                    {prefix}
                  </Text>
                </span>
              ) : (
                prefix
              )}
            </>
          )}
          <styled.input className="h a1 o de p e9"
            autoFocus={autoFocus}
            ref={(node) => mergeRefs(node, innerRef, externalRef)}
            value={actualValue}
            disabled={disabled}
            placeholder={placeholder}
            autoComplete={type === 'password' ? 'new-password' : 'off'}
            maxLength={maxLength}
            dir={direction}
            type={
              isPhone
                ? 'tel'
                : !hide && type === 'password'
                  ? 'text'
                  : type === 'email'
                    ? null
                    : type
            }
            style={{
              color: disabled
                ? colors.neutral20
                : length === 0
                  ? colors.neutral60
                  : colors.neutral100,
              ...(disabled && {
                cursor: 'not-allowed',
              }),
              ...TEXT_VARIANT_STYLES[
                size === 'small' ? 'subtext-regular' : 'display-regular'
              ],
            }}
            onChange={(e) => {
              if (isPhone) {
                const newValue = e.target.value

                if (newValue === '' || /^[0-9+]+$/.test(newValue)) {
                  setInternalValue(newValue)
                  onChange(newValue)
                }
              } else {
                // onChange(e.target.value)
                e.stopPropagation()
                setInternalValue(e.target.value)
                onChange(e.target.value)
              }
            }}
            onFocus={(e) => {
              setFocus(true)
              onFocus?.(e)
            }}
            onBlur={(e) => {
              setFocus(false)
              //    onBlur?.(() => setState(value))
              onBlur?.(e)
              if (type === 'email') {
                if (
                  e.target.value.length > 0 &&
                  (!e.target.value.includes('@') ||
                    !e.target.value.includes('.'))
                ) {
                  setInternalError(true)
                  throw new Error('please enter a valid emailaddress')
                } else {
                  setInternalError(false)
                }
              }

              if (pattern) {
                const regex = new RegExp(pattern)
                if (!regex.test(e.target.value)) {
                  setInternalError(true)
                  throw new Error('please enter a valid value')
                } else {
                  setInternalError(false)
                }
              }
            }}
            onKeyDown={onKeyDown}
          />

          {suffix && (
            <span className="a9" style={{ marginLeft: -4,}}>
              <Text
                color="inherit"
                variant={
                  size === 'small' ? 'subtext-regular' : 'display-regular'
                }
              >
                {suffix}
              </Text>
            </span>
          )}
          {keyHint && <KeyHint hint={keyHint} color="neutral" />}
          {type === 'password' && (
            <span className="a c b"
              style={{
                cursor: disabled ? 'not-allowed' : 'pointer',}}
              onClick={() => {
                if (!disabled && passwordToggle) {
                  setHide(!hide)
                }
                setTimeout(() => {
                  innerRef.current.selectionStart =
                    innerRef.current.value.length
                }, 50)
              }}
            >
              <Icon
                variant={hide ? 'show' : 'hide'}
                size={size}
                color={disabled ? 'neutral20' : 'neutral60'}
              />
            </span>
          )}
          {!disabled && clearable && type !== 'password' && length !== 0 && (
            <Stack
              align="center"
              justify="end"
              fitContent
              style={{ marginRight: '-5px' }}
            >
              <IconButton
                size="small"
                icon="close"
                tooltip="Clear"
                onClick={() => {
                  onChange('')
                }}
              />
            </Stack>
          )}
          {focus && maxLength && (
            <div className="a2 c0 gt a"
              style={{
                bottom: -5,}}
            >
              <Badge
                color={error || internalError ? 'red-fill' : 'neutral-fill'}
                style={{
                  boxShadow: shadows.popoverSmall,
                }}
              >{`${length}/${maxLength}`}</Badge>
            </div>
          )}
        </styled.div>
      </LabelDescription>
    )
  },
)

export type { TextInputProps }
export { TextInput }
